/* eslint-disable import/order -- the `react-semantic-ui-datepickers` stylesheet is a
   SIDE-EFFECT import, and the ordering this rule asks for moves module imports across
   it. Stylesheet order is cascade order, so that is a change to what the page renders,
   not to its types. */
import React, { useState, useEffect } from 'react';
import { Message, Comment, Dimmer, Loader, Dropdown, Button, Popup, Segment, Divider } from 'semantic-ui-react';

import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import type { FlatNote, NoteThread, Option, PractitionerOption } from 'types';

import { useAuthContext } from '../../../contexts/AuthContext';
import noteTypes from '../../../data/NoteTypes';
import { FHIRProxyClient, ProvenanceBuilder } from '../../../fhir-sdoh';
import * as FHIRUtils from '../../../utils/FHIRUtils';
import * as Helpers from '../../../utils/Helpers';
import type { ResourceEntry } from '../../../utils/bundleNarrowing';

import moment from 'moment';


import AddChild from './addChild';
import CreateNote from './new';

import * as fhirpath from 'fhirpath';
import type { DocumentReference, Flag, Provenance } from 'fhir/r4';


/**
 * The note-category tab this list is rendered for.
 *
 * These are the values `displayNoteThread` and the `useEffect` below branch on,
 * and they are exactly the keys of `noteCategories` plus the `TASKS_NOTES` value
 * that the `noteTypes` lookup is guarded against.
 *
 * SUSPECTED DEFECT (apps/care-betty/src/components/patient/notes/tabs.tsx:103-115):
 * `tabs.tsx` renders `NotesList` only when `type` is none of 'TASKS_NOTES',
 * 'ALL', 'CONNECT_NOTES' or 'MEMBER_DOCUMENTS' — but those four are the only
 * values any live `showTabPane` call passes; the panes that would pass 'FEEDBACK',
 * 'ACTIONABLE_INSIGHTS' and 'GENERAL' are commented out (tabs.tsx:88-99). So this
 * component appears to be unreachable in the current UI, and `noteTypes[props.type]`
 * at line 60 would be a missing-key lookup for any of the four live values.
 * REPORTED, NOT FIXED: restoring a pane or removing the component are both
 * behaviour changes.
 */
type NotesListType = 'FEEDBACK' | 'ACTIONABLE_INSIGHTS' | 'GENERAL' | 'TASKS_NOTES';

// For display of all the Notes (All categories)
function NotesList(props: {
  notesResponse: ResourceEntry<'DocumentReference'>[] | undefined;
  org: string;
  patientId: string;
  practitionerData: ResourceEntry<'Practitioner'>[] | undefined;
  refresh: () => void;
  type: NotesListType;
  // Supplied by notes/tabs.tsx but never read in this component. Declared so the
  // props type describes the call site as it actually is; typed `unknown` rather
  // than given a shape, because nothing here constrains them.
  allReferralData?: unknown;
  setNotesResponse?: unknown;
}) {
  const authInfo = useAuthContext();
  const practitionerName = authInfo.practitionerName;

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [tableData, setTableData] = useState<Record<string, NoteThread>>({});
  const [allTableData, setAllTableData] = useState<Record<string, NoteThread>>({});
  const [sortTableData, setSortTableData] = useState(false);
  const [allPractitioners, setAllPractitioners] = useState<PractitionerOption[]>([]);

  const noteCategories = {
    FEEDBACK: { code: '34109-9', display: 'Evaluation and management note' },
    ACTIONABLE_INSIGHTS: { code: '29751-5', display: 'Critical care records' },
    GENERAL: { code: '56447-6', display: 'Plan of care note' },
  };
  const [collapsed, setCollapsed] = useState(false);
  const [loaderActive, setLoaderActive] = useState(false);
  const [replyTextList, setReplyTextList] = useState({});
  const [filterNoteTypes, setFilterNoteTypes] = useState<Option[]>([]);

  let system = 'http://loinc.org';

  const sortedChildNotes: Record<string, FlatNote[]> = {};
  const allNoteType = [{ text: 'ALL', key: 'ALL', value: 'ALL' }];

  useEffect(() => {
    if (props.type === 'FEEDBACK') {
       
       
      system = 'https://projectwell.io/fhir/codes/feedback-type';
    } else if (props.type === 'ACTIONABLE_INSIGHTS') {
      system = 'https://projectwell.io/fhir/codes/ai-type';
    }

    if (props.type !== 'TASKS_NOTES') setFilterNoteTypes(allNoteType.concat(noteTypes[props.type]));
  }, [props.type]);

  useEffect(() => {
    // reset notes data on new data response
    setTableData({});
    setAllTableData({});
    if (props.notesResponse && props.notesResponse.length) {
      establishParentChildRelationship(props.notesResponse);
    }
     
   
  }, [props.notesResponse, props.type]);

  useEffect(() => {
    setValidationErrors([]);
  }, []);

  useEffect(() => {
    if (props.practitionerData && props.practitionerData) {
      const practitionersList = props.practitionerData.map((item) => {
        const practitioner = FHIRUtils.parseFHIRPractitioner(item.resource);
        const isMale = practitioner.gender.toLowerCase() === 'male' ? true : false;
        return {
          key: practitioner.id,
          text: practitioner.fullName,
          value: practitioner.id,
          image: { avatar: true, src: '/images/' + Helpers.getImage(isMale, practitioner?.fullName) },
        };
      });
      setAllPractitioners(practitionersList);
    }
  }, [props.practitionerData]);

  const toggleCollapse = (value: boolean) => {
    return !value;
  };

  const parentChildRelationships = (
    resource: DocumentReference,
    fhirNote: FlatNote,
    notesList: Record<string, NoteThread>,
    replyNoteIds: Record<string, boolean>
  ) => {
    fhirNote.date = fhirNote.date && moment(fhirNote.date, 'YYYY-MM-DD').format('MMMM Do, YYYY');

    const referenceStr = fhirpath.evaluate(resource, 'relatesTo.first().target.reference').toString();

    const reference = referenceStr && referenceStr.split('/')[1];
    if (reference !== '' && reference !== undefined) {
      const parsedChildNote = FHIRUtils.parseFHIRNotes(resource);
      replyNoteIds[parsedChildNote.id] = false;
      if (Object.prototype.hasOwnProperty.call(notesList, reference)) {
        // The `hasOwnProperty` test above is what makes this read safe at
        // runtime; noUncheckedIndexedAccess cannot see through it.
        (notesList[reference] as NoteThread).child.push(parsedChildNote);
        setReplyTextList((prevState) => ({
          ...prevState,
          [reference]: '',
        }));
      } else {
        notesList[reference] = { parent: {}, child: [] };
        notesList[reference].child.push(parsedChildNote);
        setReplyTextList((prevState) => ({
          ...prevState,
          [reference]: '',
        }));
      }
    } else {
      // `DocumentReference.id` is `0..1`, and it is used as a computed key
      // unguarded, so an absent id produces the literal key "undefined"; the
      // assertion preserves that rather than filtering it out.
      const resourceId = resource.id as string;
      replyNoteIds[resourceId] = false;
      if (Object.prototype.hasOwnProperty.call(notesList, resourceId)) {
        // Guarded by `hasOwnProperty` above, so the spread source is present and
        // still carries `parent`/`child`; the assertion keeps those required.
        notesList[resourceId] = {
          ...(notesList[resourceId] as NoteThread),
          ...FHIRUtils.parseFHIRNotes(resource),
        };
        setReplyTextList((prevState) => ({
          ...prevState,
          [resourceId]: '',
        }));
      } else {
        notesList[resourceId] = { parent: {}, child: [] };
        notesList[resourceId] = {
          ...notesList[resourceId],
          ...FHIRUtils.parseFHIRNotes(resource),
        };
        setReplyTextList((prevState) => ({
          ...prevState,
          [resourceId]: '',
        }));
      }
    }
  };

  const establishParentChildRelationship = (notesData: ResourceEntry<'DocumentReference'>[]) => {
    const notesList: Record<string, NoteThread> = {};
    const replyNoteIds: Record<string, boolean> = {};

    notesData.forEach((note) => {
      const resource = note.resource;
      const fhirNote = FHIRUtils.parseFHIRNotes(resource);
      parentChildRelationships(resource, fhirNote, notesList, replyNoteIds);
    });

    for (const noteThread in notesList) {
      // `for...in` only yields keys that exist, so this entry is present.
      const child = (notesList[noteThread] as NoteThread).child;

      child.sort(function (a, b) {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });
      (notesList[noteThread] as NoteThread).child = child;
    }

    setTableData((prevState) => ({
      ...prevState,
      ...notesList,
    }));

    setAllTableData((prevState) => ({
      ...prevState,
      ...notesList,
    }));
  };

  //FHIR Call to create a Child Note
  const createNote = (
    type: string,
    notePayload: DocumentReference,
    parentId: string,
    flagpayload: Flag | undefined = undefined,
    additionalflagpayload: Flag | undefined = undefined
  ) => {
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `DocumentReference/${notePayload.id!}`,
      {
        reference: 'Practitioner/' + authInfo.practitionerId,
        display: practitionerName,
      },
      props.patientId,
      'CREATE',
      `A Child note (${notePayload.description}) has been created to ${type} task`,
      { code: props.org.toUpperCase(), display: props.org.toLowerCase() } //eg. {Organization/HUMANA,humana}
    );

    //now post bundle request
    let fhirBundle;
    if (flagpayload) {
      if (additionalflagpayload) {
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle(
          [notePayload, flagpayload, additionalflagpayload],
          provenance
        );
      } else {
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle([notePayload, flagpayload], provenance);
      }
    } else {
      if (additionalflagpayload) {
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle([notePayload, additionalflagpayload], provenance);
      } else {
        fhirBundle = ProvenanceBuilder.buildProvenanceBundle([notePayload], provenance);
      }
    }
    setLoaderActive(true);

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- the value is a `fhir-sdoh` export whose module ships no usable types */
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        setLoaderActive(false);
        //(clear reply text id after adding child)
        setReplyTextList((prevState) => ({
          ...prevState,
          [parentId]: '',
        }));
        // calling mutate
        props.refresh();
      },
      (error: { toString(): string }) => {
        setLoaderActive(false);
        setValidationErrors((state) => [...state, 'Unable to create note: ' + error.toString()]);
      }
    );
  };

  // Populate validation/error messages on the form
  const setValidationMessages = () => {
    return validationErrors.map((error) => {
      return <Message.Item key={error}>{error}</Message.Item>;
    });
  };

  const displayChild = (child: FlatNote) => {
    return (
      <div key={child.id}>
        <Comment.Group collapsed={collapsed}>
          <Comment>
            {/* `CommentAvatarProps.src` is required `string`. A practitioner absent
                from the list makes `findIndex` return -1, so the subscript is
                `undefined` and the avatar renders with no image — which is what it
                does today. Same at the second avatar in this file. */}
            <Comment.Avatar
              as="a"
              src={
                allPractitioners[
                  allPractitioners.findIndex((practitioner) => practitioner.text === child.author?.[0])
                ]?.image?.src as string
              }
            />
            <Comment.Content>
              <Comment.Author as="a">{child.description}</Comment.Author>
              <Comment.Metadata></Comment.Metadata>
              <Comment.Actions>
                <Comment.Text>
                  <div className="noteattr_display_text">
                    {child.author}. {moment(child.date).format('MM-DD-YYYY')}
                  </div>
                </Comment.Text>
              </Comment.Actions>
            </Comment.Content>
          </Comment>
        </Comment.Group>
      </div>
    );
  };

  const displayReplyText = (key: string, type: string, code: string) => {
    return (
      <AddChild
        patientId={props.patientId}
        parentId={key}
        createNote={createNote}
        type={type}
        notesType={props.type}
        code={code}
        replyTextList={replyTextList}
        setReplyTextList={setReplyTextList}
        noteCategories={noteCategories}
        org={props.org}
        practitionerData={allPractitioners}
        childNote={sortedChildNotes}
        tableData={tableData}
      />
    );
  };

  const filterNotes = (e: React.MouseEvent<HTMLDivElement>) => {
    // `e.target` is typed `EventTarget`; this handler is bound to a Dropdown.Menu
    // whose click target is always a DOM node, so `textContent` is read off it
    // directly.
    const clickedText = (e.target as Node).textContent;
    const filteredTableData: Record<string, NoteThread> = {};
    if (clickedText === 'ALL' || clickedText === '') {
      setTableData(allTableData);
    } else {
      for (const key in allTableData) {
        // `for...in` only yields keys that exist, so this entry is present.
        const entry = allTableData[key] as NoteThread;
        if (entry.type === clickedText) {
          filteredTableData[key] = entry;
        }
      }
      setTableData(filteredTableData);
    }
  };

  const sortNotes = (_e: React.MouseEvent<HTMLButtonElement>) => {
    setSortTableData(!sortTableData);
  };

  const displayNoteThread = (key: string, tableData: Record<string, NoteThread>) => {
    // Every read below dereferences `tableData[key]` unguarded; the assertion
    // preserves that.
    const thread = tableData[key] as NoteThread;
    // Each branch's `props.type === '...'` test narrows `props.type` to a literal
    // that is a real key of `noteCategories`, so these lookups are total.
    if (props.type === 'FEEDBACK' && thread.categoryCode !== noteCategories[props.type].code) {
      return '';
    } else if (props.type === 'ACTIONABLE_INSIGHTS' && thread.categoryCode !== noteCategories[props.type].code) {
      return '';
    } else if (props.type === 'GENERAL' && thread.categoryCode !== noteCategories[props.type].code) {
      return '';
    }

    return (
      <Comment key={key}>
        <Comment.Avatar
          as="a"
          src={
            allPractitioners[
              allPractitioners.findIndex((practitioner) => practitioner.text.trim() === thread.author?.[0])
            ]?.image?.src as string
          }
        />

        <Comment.Content>
          <Comment.Author as="a">{thread.description}</Comment.Author>
          <Comment.Metadata></Comment.Metadata>
          <Comment.Actions>
            <Comment.Text>
              <div className="noteattr_display_text">
                <span className="note_type_text">{thread.type}</span> added by {thread.author}.{' '}
                {moment(thread.date).format('MM-DD-YYYY')}
              </div>
            </Comment.Text>
            <a>Reply</a>
            {/* `FlatNote.type` and `.code` are both optional; the reply control
                keys its new-note payload on them, so a thread missing either posts
                a reply carrying `undefined` in that field. */}
            {displayReplyText(key, thread.type!, thread.code!)}
          </Comment.Actions>
          {(sortedChildNotes[key] as FlatNote[]).map((child) => displayChild(child))}
        </Comment.Content>
      </Comment>
    );
  };

  const sortedParentNotes = Object.keys(tableData).sort(function (a, b) {
    // `tableData[x]` is `NoteThread | undefined` under noUncheckedIndexedAccess,
    // and `date` is itself optional on a thread seeded from a reply. Both are
    // read unguarded — `new Date(undefined)` is Invalid Date, so `getTime()`
    // is NaN and the comparison keeps the incoming order.
    const dateOf = (thread: NoteThread | undefined) => new Date((thread as NoteThread).date as string).getTime();
    if (sortTableData) {
      return dateOf(tableData[b]) - dateOf(tableData[a]);
    } else {
      return dateOf(tableData[a]) - dateOf(tableData[b]);
    }
  });

  sortedParentNotes.map((parentKey) => (sortedChildNotes[parentKey] = (tableData[parentKey] as NoteThread).child));

  return (
    <div>
      <Dimmer active={loaderActive} inverted>
        <Loader></Loader>
      </Dimmer>

      {validationErrors.length > 0 && (
        <Message error>
          <Message.Header>Error:</Message.Header>
          <Message.List>{setValidationMessages()}</Message.List>
        </Message>
      )}

      <div>
        <CreateNote
          practitionerData={allPractitioners}
          setValidationErrors={setValidationErrors}
          patientId={props.patientId}
          token={authInfo.token}
          type={props.type}
          refresh={props.refresh}
          /*
           * SUSPECTED DEFECT (pre-existing): `NotesListType` admits `TASKS_NOTES`,
           * but `data/NoteTypes` carries no such key, so this would read
           * `undefined` and `CreateNote` would render an empty Note Type
           * dropdown. Unreachable today only because `notes/tabs.tsx` excludes
           * `TASKS_NOTES` before rendering this component; the prop type is what
           * still permits it. Asserted rather than narrowed so the guard stays
           * the only thing deciding which types reach here.
           */
          noteTypes={noteTypes[props.type as keyof typeof noteTypes]}
          system={system}
          noteCategories={noteCategories}
          org={props.org}
        />
      </div>
      <Segment>
        <div className="cb_height_30">
          <span className="cb_floatleft">
            <Popup
              content="Sort"
              trigger={<Button circular onClick={sortNotes} name="all" icon="sort" color="teal" />}
            />
            <Popup
              content="Collapse"
              trigger={
                <Button
                  onClick={(_e) => setCollapsed(toggleCollapse)}
                  circular
                  name="collapse"
                  icon="angle double down"
                  color="orange"
                />
              }
            />
            <Dropdown icon="filter" floating button className="icon">
              <Dropdown.Menu>
                <Dropdown.Header icon="filter" content="Filter by Type" />
                <Dropdown.Divider />
                <Dropdown.Menu onClick={(e: React.MouseEvent<HTMLDivElement>) => filterNotes(e)} scrolling>
                  {filterNoteTypes.map((option) => (
                    // `Option` carries its own `key`, so the spread looks like it
                    // overwrites the explicit one. It does not: React extracts `key`
                    // off the JSX attributes before the spread is applied, so the
                    // explicit `option.value` is the key and the spread's is inert.
                    // @ts-expect-error TS2783
                    <Dropdown.Item key={option.value} {...option} />
                  ))}
                </Dropdown.Menu>
              </Dropdown.Menu>
            </Dropdown>
          </span>
        </div>
        <Divider />

        <Comment.Group>{sortedParentNotes.map((key) => displayNoteThread(key, tableData))}</Comment.Group>
      </Segment>
    </div>
  );
}

export default NotesList;
